import { useState } from "react";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { MenuItem, ManagementIcon } from "@flat-ui";
import { GroupMembersProvider } from "@context-providers/group-members-provider";
import { ChangeManagementDialogContent } from "@pages/project-details/project-overview/change-management/change-management-dialog-content";
import { SdbProject } from "@custom-types/project-types";
import { useCompanyId } from "@context-providers/company-id-provider";
import { getProjectByIdSelector } from "@store/projects/projects-selector";
import { fetchProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";

interface ChangeManagementButtonMobileProps {
  /** The project to manage */
  project: SdbProject;
  /** Callback invoked when the dialog is closed */
  onClose: () => void;
}

/**
 * Wrapper that renders a menu item to open the change management dialog.
 * When clicked, opens the dialog with the necessary GroupMembersProvider context.
 */
export function ChangeManagementButtonMobile({
  project,
  onClose,
}: ChangeManagementButtonMobileProps): JSX.Element {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const { handleErrorWithToast } = useErrorContext();
  const projectDetails = useAppSelector(getProjectByIdSelector(project.id));

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFetchingProject, setIsFetchingProject] = useState(false);

  // Use project details from the store when available (contains group data);
  // otherwise fall back to the incoming project prop.
  const hydratedProject = projectDetails?.group?.id ? projectDetails : project;

  /**
   * Opens the change management dialog.
   * If the project details are not fully loaded, it fetches them first.
   */
  async function handleOpenDialog(): Promise<void> {
    if (hydratedProject.group?.id) {
      // No need to fetch project details again.
      setIsDialogOpen(true);
      return;
    }

    try {
      setIsFetchingProject(true);
      await dispatch(
        fetchProjectDetails({ companyId, projectId: project.id })
      ).unwrap();
    } catch (error) {
      handleErrorWithToast({
        id: `fetchProjectDetails-${project.id}`,
        title: "Could not load project details",
        error,
      });
    } finally {
      setIsFetchingProject(false);
    }

    setIsDialogOpen(true);
  }

  function handleCloseDialog(): void {
    setIsDialogOpen(false);
    onClose();
  }

  return (
    <>
      <MenuItem
        label="Management"
        Icon={ManagementIcon}
        disabled={isFetchingProject}
        onClick={handleOpenDialog}
      />
      {isDialogOpen && (
        <GroupMembersProvider project={hydratedProject}>
          <ChangeManagementDialogContent
            isOpen={isDialogOpen}
            onClose={handleCloseDialog}
          />
        </GroupMembersProvider>
      )}
    </>
  );
}
